import { useState, useEffect } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchApi } from '../api/client';

function formatCurrency(value) {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const STRATEGY_LABELS = {
  vertical_call_debit: 'Bull Call Spread',
  vertical_call_credit: 'Bear Call Spread',
  vertical_put_debit: 'Bear Put Spread',
  vertical_put_credit: 'Bull Put Spread',
  long_call: 'Long Call',
  long_put: 'Long Put',
  short_call_covered: 'Covered Call',
  short_put_cash_secured: 'Cash-Secured Put',
  straddle: 'Straddle',
  strangle: 'Strangle',
  iron_condor: 'Iron Condor',
  iron_butterfly: 'Iron Butterfly',
  calendar_spread: 'Calendar Spread',
  diagonal_spread: 'Diagonal Spread',
  custom: 'Custom',
  unmatched: 'Unmatched',
};

function getDteColor(dte) {
  if (dte === null || dte === undefined) return 'text-slate-400';
  if (dte <= 7) return 'text-red-400';
  if (dte <= 21) return 'text-amber-400';
  return 'text-slate-300';
}

function SpreadRow({ spread }) {
  const [expanded, setExpanded] = useState(false);
  const pnl = spread.unrealized_pnl;
  const pnlColor = pnl > 0 ? 'text-emerald-400' : pnl < 0 ? 'text-red-400' : 'text-slate-400';
  const pnlIcon = pnl > 0 ? TrendingUp : pnl < 0 ? TrendingDown : null;
  const PnlIcon = pnlIcon;

  const pnlPct = spread.net_debit_credit !== 0
    ? (pnl / Math.abs(spread.net_debit_credit)) * 100
    : 0;

  return (
    <>
      <tr
        className="border-b border-slate-700/50 hover:bg-slate-800/30 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1.5">
            {expanded
              ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
            }
            <span className="font-medium text-sky-400">{spread.underlying_ticker}</span>
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-slate-300">
          {STRATEGY_LABELS[spread.strategy_type] || spread.strategy_type}
        </td>
        <td className="px-3 py-2.5 text-sm font-mono text-slate-300">
          {spread.leg_summary}
        </td>
        <td className="px-3 py-2.5 text-sm text-slate-300">
          {spread.expiration_date}
        </td>
        <td className={`px-3 py-2.5 text-sm font-mono ${getDteColor(spread.dte)}`}>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {spread.dte}d
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm font-mono text-slate-300 text-right">
          {formatCurrency(spread.net_debit_credit)}
        </td>
        <td className="px-3 py-2.5 text-sm font-mono text-slate-300 text-right">
          {formatCurrency(spread.current_value)}
        </td>
        <td className={`px-3 py-2.5 text-sm font-mono text-right ${pnlColor}`}>
          <div className="flex items-center justify-end gap-1">
            {PnlIcon && <PnlIcon className="w-3 h-3" />}
            {formatCurrency(pnl)}
          </div>
          <div className={`text-xs ${pnlColor}`}>
            {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-right">
          {spread.max_profit != null && (
            <span className="text-emerald-400">{formatCurrency(spread.max_profit)}</span>
          )}
        </td>
        <td className="px-3 py-2.5 text-sm text-right">
          {spread.max_loss != null && (
            <span className="text-red-400">{formatCurrency(spread.max_loss)}</span>
          )}
        </td>
      </tr>
      {expanded && spread.legs?.map(leg => (
        <tr key={leg.id} className="bg-slate-800/20 border-b border-slate-700/30">
          <td className="pl-9 pr-3 py-1.5 text-xs text-slate-500">
            {leg.quantity > 0 ? 'LONG' : 'SHORT'}
          </td>
          <td className="px-3 py-1.5 text-xs font-mono text-slate-400" colSpan={2}>
            {leg.contract_symbol}
          </td>
          <td className="px-3 py-1.5 text-xs text-slate-400">
            {leg.option_type.toUpperCase()} ${leg.strike_price}
          </td>
          <td className="px-3 py-1.5 text-xs text-slate-400">
            qty: {Math.abs(leg.quantity)}
          </td>
          <td className="px-3 py-1.5 text-xs font-mono text-slate-400 text-right">
            {formatCurrency(leg.cost_basis)}
          </td>
          <td className="px-3 py-1.5 text-xs font-mono text-slate-400 text-right">
            {formatCurrency(leg.market_value)}
          </td>
          <td colSpan={3}></td>
        </tr>
      ))}
    </>
  );
}


export default function OptionsTable() {
  const [spreads, setSpreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi('/options/spreads')
      .then(data => setSpreads(data?.spreads || []))
      .catch(err => {
        console.error('Options fetch error:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card">
        <h3 className="card-header">
          <TrendingUp className="w-5 h-5 text-violet-400" />
          Options Positions
        </h3>
        <div className="flex justify-center py-8">
          <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h3 className="card-header">
          <TrendingUp className="w-5 h-5 text-violet-400" />
          Options Positions
        </h3>
        <p className="text-sm text-red-400">Failed to load options: {error}</p>
      </div>
    );
  }

  if (spreads.length === 0) return null;

  const totalValue = spreads.reduce((sum, s) => sum + (s.current_value || 0), 0);
  const totalPnl = spreads.reduce((sum, s) => sum + (s.unrealized_pnl || 0), 0);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="card-header mb-0">
          <TrendingUp className="w-5 h-5 text-violet-400" />
          Options Positions
          <span className="ml-2 text-xs text-slate-500 font-normal">
            {spreads.length} spread{spreads.length !== 1 ? 's' : ''}
          </span>
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-400">
            Value: <span className="font-mono text-slate-200">{formatCurrency(totalValue)}</span>
          </span>
          <span className="text-slate-400">
            P&L:{' '}
            <span className={`font-mono ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {totalPnl >= 0 ? '+' : ''}{formatCurrency(totalPnl)}
            </span>
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2">Ticker</th>
              <th className="px-3 py-2">Strategy</th>
              <th className="px-3 py-2">Strikes</th>
              <th className="px-3 py-2">Expiration</th>
              <th className="px-3 py-2">DTE</th>
              <th className="px-3 py-2 text-right">Net Cost</th>
              <th className="px-3 py-2 text-right">Value</th>
              <th className="px-3 py-2 text-right">P&L</th>
              <th className="px-3 py-2 text-right">Max Profit</th>
              <th className="px-3 py-2 text-right">Max Loss</th>
            </tr>
          </thead>
          <tbody>
            {spreads.map(spread => (
              <SpreadRow key={spread.id} spread={spread} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
